import React, { useMemo } from "react";
import { Alert, Space, Tag, Tooltip, Typography, theme } from "antd";
import { ExportOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";

import { fetchProteinStructure } from "../../../../api/geneCodes/ProteinStructureApi";
import { ProteinStructureResponse } from "../../../../types/geneCodes/ProteinStructureTypes";
import { Model3DViewer, Model3DLineSet, Model3DMeshSet, Model3DPointSet, Model3DBounds } from "../../../../components/shared/Model3DViewer";
import { ColorRamp, buildCartoonModel } from "../../../../components/shared/Model3DViewer/representations/cartoon";
import { EmptyState, InlineLoading } from "../../../../components/shared";
import "./ProteinStructurePanel.css";

const { Text, Link } = Typography;

// CPK convention — element identity, not theme colour. Values are rgb triples in 0..1.
const ELEMENT_COLORS: Record<string, [number, number, number]> = {
	C: [0.55, 0.56, 0.58],
	N: [0.19, 0.31, 0.97],
	O: [1.0, 0.2, 0.19],
	S: [1.0, 0.86, 0.19],
	P: [1.0, 0.5, 0.0],
};
const UNKNOWN_ELEMENT_COLOR: [number, number, number] = [0.6, 0.6, 0.6];

// Covalent bonds inside a residue and across the peptide link all sit under 2 A; consecutive
// alpha carbons sit near 3.8 A, so a CA-only trace needs the wider cutoff.
const COVALENT_MAX_ANGSTROM = 2.0;
const TRACE_MAX_ANGSTROM = 4.5;

interface ViewerModel {
	points: Model3DPointSet[];
	lines: Model3DLineSet[];
	meshes: Model3DMeshSet[];
	bounds: Model3DBounds | null;
}

export interface ViewerModelOptions {
	ramp?: ColorRamp;
	mode?: "cartoon" | "backbone";
}

/** Ribbons first; the backbone primitives stay as the fallback for a payload too thin to spline. */
export const buildViewerModel = (data: ProteinStructureResponse, options: ViewerModelOptions = {}): ViewerModel | null => {
	const atoms = data.atoms;
	if (!atoms || atoms.x.length === 0) return null;

	const bounds = data.bounds ? { min: data.bounds.min, max: data.bounds.max, center: data.bounds.center } : null;

	if (options.mode !== "backbone") {
		const meshes = buildCartoonModel(atoms, { ramp: options.ramp });
		if (meshes.length) return { points: [], lines: [], meshes, bounds };
	}

	const count = atoms.x.length;
	const positions: number[] = new Array(count * 3);
	const colors: number[] = new Array(count * 3);

	for (let i = 0; i < count; i += 1) {
		positions[i * 3] = atoms.x[i];
		positions[i * 3 + 1] = atoms.y[i];
		positions[i * 3 + 2] = atoms.z[i];

		const rgb = ELEMENT_COLORS[atoms.element[i]] ?? UNKNOWN_ELEMENT_COLOR;
		colors[i * 3] = rgb[0];
		colors[i * 3 + 1] = rgb[1];
		colors[i * 3 + 2] = rgb[2];
	}

	const trace = data.representation === "trace";
	// Backbone order is N, CA, C, O, so the peptide bond joins i to i+2 across the carbonyl oxygen.
	const span = trace ? 1 : 2;
	const maxBond = trace ? TRACE_MAX_ANGSTROM : COVALENT_MAX_ANGSTROM;
	const maxBondSquared = maxBond * maxBond;

	const linePositions: number[] = [];
	const lineColors: number[] = [];

	for (let i = 0; i < count; i += 1) {
		for (let step = 1; step <= span; step += 1) {
			const j = i + step;
			if (j >= count || atoms.chain[j] !== atoms.chain[i]) continue;

			const dx = atoms.x[j] - atoms.x[i];
			const dy = atoms.y[j] - atoms.y[i];
			const dz = atoms.z[j] - atoms.z[i];
			if (dx * dx + dy * dy + dz * dz > maxBondSquared) continue;

			linePositions.push(atoms.x[i], atoms.y[i], atoms.z[i], atoms.x[j], atoms.y[j], atoms.z[j]);
			lineColors.push(
				colors[i * 3],
				colors[i * 3 + 1],
				colors[i * 3 + 2],
				colors[j * 3],
				colors[j * 3 + 1],
				colors[j * 3 + 2],
			);
		}
	}

	return {
		points: [{ id: "backbone-points", positions, colors, radius: trace ? 0.6 : 0.32 }],
		lines: linePositions.length ? [{ id: "backbone-bonds", positions: linePositions, colors: lineColors }] : [],
		meshes: [],
		bounds,
	};
};

const parseColor = (value?: string): [number, number, number] | null => {
	if (!value) return null;

	const short = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(value.trim());
	if (short) return [1, 2, 3].map((i) => parseInt(short[i] + short[i], 16) / 255) as [number, number, number];

	const full = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})/i.exec(value.trim());
	if (full) return [1, 2, 3].map((i) => parseInt(full[i], 16) / 255) as [number, number, number];

	const rgb = /^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/i.exec(value.trim());
	if (rgb) return [1, 2, 3].map((i) => Number(rgb[i]) / 255) as [number, number, number];

	return null;
};

/** Cool to warm across the b-factor range — rigid to flexible, in the clinic's own accent colours. */
const rampFrom = (info?: string, warning?: string, error?: string): ColorRamp | undefined => {
	const low = parseColor(info);
	const mid = parseColor(warning);
	const high = parseColor(error);

	return low && mid && high ? { low, mid, high } : undefined;
};

const plural = (count: number, one: string, many: string): string => `${count} ${count === 1 ? one : many}`;

interface ProteinStructurePanelProps {
	geneName: string | null;
	height?: number;
}

const ProteinStructurePanel: React.FC<ProteinStructurePanelProps> = ({ geneName, height = 340 }) => {
	const { token } = theme.useToken();

	const { data, isLoading, isError } = useQuery({
		queryKey: ["proteinStructure", geneName],
		queryFn: () => fetchProteinStructure(geneName as string),
		enabled: Boolean(geneName),
		staleTime: 1000 * 60 * 60,
		retry: 1,
	});

	const ramp = useMemo(() => rampFrom(token.colorInfo, token.colorWarning, token.colorError), [token]);
	const model = useMemo(() => (data ? buildViewerModel(data, { ramp }) : null), [data, ramp]);

	if (!geneName) {
		return <EmptyState title="No gene selected" description="Select a gene in the wheel to see its protein structure." />;
	}

	if (isLoading) {
		return <InlineLoading message="Loading protein structure…" minHeight={height} />;
	}

	// A lookup failure must never take the rest of the gene tab with it.
	if (isError || !data) {
		return (
			<Alert
				type="warning"
				showIcon
				message="Protein structure unavailable"
				description="The structure service could not be reached. The rest of this gene's data is unaffected."
			/>
		);
	}

	if (!data.found || !model) {
		return (
			<EmptyState
				title={`No published structure for ${data.gene}`}
				description="This gene has no experimentally determined structure in the Protein Data Bank."
			/>
		);
	}

	return (
		<div className="protein-structure-panel" data-testid="protein-structure-panel">
			<Space size={8} wrap className="protein-structure-panel__meta">
				<Link href={data.rcsb_url ?? undefined} target="_blank" rel="noreferrer" data-testid="protein-structure-rcsb-link">
					<Space size={4}>
						<Text strong className="protein-structure-panel__title">
							PDB {data.pdb_id}
						</Text>
						<ExportOutlined className="protein-structure-panel__icon" />
					</Space>
				</Link>
				{data.title && (
					<Text type="secondary" className="protein-structure-panel__caption">
						{data.title}
					</Text>
				)}
				<Tooltip title={data.representation === "trace" ? "Alpha-carbon trace" : "Backbone: N, CA, C and O"}>
					<Tag color={token.colorPrimary} className="protein-structure-panel__tag">
						{data.representation === "trace" ? "CA trace" : "Backbone"}
					</Tag>
				</Tooltip>
				{data.secondary_structure && (
					<Text type="secondary" className="protein-structure-panel__caption" data-testid="protein-structure-ss-summary">
						{data.secondary_structure.source === "records"
							? `${plural(data.secondary_structure.helix_count, "helix", "helices")} · ${plural(data.secondary_structure.sheet_count, "strand", "strands")}`
							: "No secondary structure recorded"}
					</Text>
				)}
				<Text type="secondary" className="protein-structure-panel__caption">
					{data.returned_count.toLocaleString()} of {data.atom_count.toLocaleString()} atoms
					{data.chains.length ? ` · chain${data.chains.length > 1 ? "s" : ""} ${data.chains.join(", ")}` : ""}
				</Text>
			</Space>

			{data.truncated && (
				<Alert
					type="info"
					showIcon
					className="protein-structure-panel__notice"
					message="This structure is too large to render in full — only part of it is shown."
				/>
			)}

			<Model3DViewer
				points={model.points}
				lines={model.lines}
				meshes={model.meshes}
				bounds={model.bounds}
				height={height}
				ariaLabel={`3D structure of the protein encoded by ${data.gene}`}
			/>
		</div>
	);
};

export default ProteinStructurePanel;
